"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.changeHistorySchema = exports.changeHistoryEntrySchema = exports.changeHistoryQuerySchema = exports.changeHistoryRecordPathSchema = exports.changeHistoryEntitySchema = exports.CHANGE_HISTORY_ENTITIES = void 0;
/**
 * `RPM-063` — a configuration record's change history, read back from the administrative change
 * trail (`admin_change_events`, `RPM-055`) on the record's own screen (`J-291`).
 *
 * ONE ENTRY PER CHANGE, NOT ONE PER TRAIL ROW. A facility profile is versioned: a zone change closes
 * the current row and inserts its successor, two trail rows in one transaction. The history pairs
 * them into one entry whose Before is the old version and whose After is the new one. A routing
 * contact's history carries its endpoints' changes beside its own, each named by `subject`. A band
 * set's history is the tenant's: every version, and each publish is one entry — the new version, its
 * threshold rows and its predecessor's close, written in one transaction — named by its version.
 *
 * BEFORE AND AFTER CARRY THE CHANGED COLUMNS ONLY, keyed by the trail's own column names. A JSON
 * value is returned whole, per column, and the screen lists only the keys within it that changed
 * (row 3). The trail never holds PHI: no PHI table is enrolled (`RPM-055`, §7.7(j)), and the actor
 * is a member of staff, named by display name.
 *
 * NO PARAMETER IS AN ATTRIBUTION FIELD, and the cursor is opaque (`rpm-readings.ts` says why).
 */
const zod_1 = require("zod");
const rpm_enums_js_1 = require("./db/rpm-enums.js");
/** The administered records with a history, by the path segment that names each. */
exports.CHANGE_HISTORY_ENTITIES = [
    // The tenant's band sets (`RPM-058`, `J-292`): every version, one entry per publish.
    "band-sets",
    "care-units",
    "facility-routing",
    "facility-profiles",
    // The message-content lines (`RPM-092`, `J-296`): a tenant default or one building's own line.
    "notification-attributes",
    "routing-contacts",
    "standing-ccs",
    // `RPM-092`'s other surfaces (`J-296`): an upstream platform, a role assignment and a setting.
    "source-systems",
    "role-assignments",
    "practice-configurations",
];
exports.changeHistoryEntitySchema = zod_1.z.enum(exports.CHANGE_HISTORY_ENTITIES);
/** A care unit, a contact or a standing copy is named by its uuid; a building's records by the mirror id. */
exports.changeHistoryRecordPathSchema = zod_1.z.object({
    recordId: zod_1.z.string().min(1).max(64),
});
exports.changeHistoryQuerySchema = zod_1.z.strictObject({
    limit: zod_1.z.coerce.number().int().min(1).max(100).default(25),
    /** Opaque. The server issues it and the server reads it. */
    cursor: zod_1.z
        .string()
        .regex(/^\d{1,18}$/)
        .optional(),
});
exports.changeHistoryEntrySchema = zod_1.z.object({
    /** The trail row's id, or the two ids of a versioned change joined by `+`. */
    id: zod_1.z.string(),
    operation: zod_1.z.enum(rpm_enums_js_1.changeOperation.enumValues),
    /** Which record of the history the change was to, when it covers several ("Endpoint"). */
    subject: zod_1.z.string().nullable(),
    changedColumns: zod_1.z.array(zod_1.z.string()),
    before: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).nullable(),
    after: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).nullable(),
    /** The account's display name; its email only when it has no name. */
    actorName: zod_1.z.string(),
    channel: zod_1.z.enum(rpm_enums_js_1.changeChannel.enumValues),
    reason: zod_1.z.string().nullable(),
    occurredAt: zod_1.z.iso.datetime(),
});
exports.changeHistorySchema = zod_1.z.object({
    entity: exports.changeHistoryEntitySchema,
    recordId: zod_1.z.string(),
    /** The record in words, for the table's caption: a care unit's label, a building's name. */
    label: zod_1.z.string(),
    /** The building the record belongs to; null for a tenant-wide record (a standing copy). */
    facilityId: zod_1.z.string().nullable(),
    /** The building's operative zone, for local time; null when it has none or the record has no building. */
    timeZone: zod_1.z.string().nullable(),
    /** Newest first. */
    items: zod_1.z.array(exports.changeHistoryEntrySchema),
    /** Pass as `cursor` for the next, older page; null on the last page. */
    nextCursor: zod_1.z.string().nullable(),
});
//# sourceMappingURL=rpm-change-history.js.map